import React, { useRef, useState } from "react";
import { Upload, X, Loader2, Image as ImageIcon } from "lucide-react";
import api from "../lib/api";

/**
 * Image upload + URL input combo. Student may either paste an external
 * URL or upload a JPG/PNG/WebP (<=5MB) that gets stored in Emergent
 * Object Storage and served back via /api/files/{path}. The returned
 * URL is persisted directly in the event/announcement document.
 */
export default function ImageUploadField({
  value,
  onChange,
  testId = "image-upload",
  placeholder = "Cole a URL ou envie uma imagem",
}) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const handlePick = () => inputRef.current?.click();

  const handleFile = async (e) => {
    setError("");
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file later
    if (!file) return;
    if (!["image/jpeg", "image/jpg", "image/png", "image/webp"].includes(file.type)) {
      setError("Use JPG, PNG ou WebP.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError("Imagem maior que 5 MB.");
      return;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await api.post("/api/uploads/image", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(res.data.url);
    } catch (err) {
      setError(err?.response?.data?.detail || "Falha no upload.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-1.5">
      <div className="flex gap-2">
        <input
          data-testid={`${testId}-url`}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="flex-1 bg-[#0F1218] border border-[#282D3D] rounded-lg px-2 py-2 text-white text-sm focus:outline-none focus:border-[#FF6B00]"
        />
        <button
          type="button"
          data-testid={`${testId}-btn`}
          onClick={handlePick}
          disabled={busy}
          title="Enviar imagem (JPG, PNG ou WebP, até 5 MB)"
          className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#1A1D26] border border-[#3E4760] hover:border-[#FF6B00] text-gray-200 text-xs font-bold disabled:opacity-60"
        >
          {busy ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Upload className="w-3.5 h-3.5" />
          )}
          <span className="hidden sm:inline">Enviar</span>
        </button>
        {value ? (
          <button
            type="button"
            data-testid={`${testId}-clear`}
            onClick={() => onChange("")}
            title="Remover"
            className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-lg bg-[#1A1D26] border border-[#3E4760] hover:border-red-400 text-gray-400 hover:text-red-300"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        ) : null}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp"
        className="hidden"
        onChange={handleFile}
      />
      {error ? (
        <p data-testid={`${testId}-error`} className="text-xs text-red-400">
          {error}
        </p>
      ) : (
        <p className="text-[10px] text-gray-500">
          JPG, PNG ou WebP até 5 MB. Não cole links do Google Drive — eles abrem como HTML.
        </p>
      )}
      {value ? (
        <div className="rounded-lg overflow-hidden border border-[#282D3D] bg-[#0B0D12]">
          <img
            data-testid={`${testId}-preview`}
            src={value}
            alt="Prévia"
            onError={(e) => {
              e.currentTarget.style.display = "none";
              const sib = e.currentTarget.nextElementSibling;
              if (sib) sib.style.display = "flex";
            }}
            className="w-full max-h-40 object-cover"
          />
          <div
            data-testid={`${testId}-broken`}
            style={{ display: "none" }}
            className="w-full h-24 items-center justify-center gap-2 text-amber-300 text-xs bg-amber-500/10"
          >
            <ImageIcon className="w-4 h-4" />
            Não foi possível carregar essa URL. Use o botão Enviar.
          </div>
        </div>
      ) : null}
    </div>
  );
}
